import {useState} from "react";
import {Link, useNavigate, useLocation} from "react-router-dom";
import {message} from "antd";
import {useDispatch} from "react-redux";
import {reduxLogin} from "../../RTK/Slices/authSlice";
import {useLoginMutation} from "../../RTK/Slices/Slices/apiSliceMeat";
import {transferGuestDataToUser} from "../../utils/localStorage";
import {language} from "../../language/Language";
import {Helmet} from "react-helmet";
import Logo from "../../assets/Logo.png";

const texts = {
    ar: {
        registerNew: "تسجيل حساب جديد",
        registerNewSub: "اضغط هنا لو أول مرة",
        existingAccount: "دخول حساب مسجل سابقاً",
        username: "رقم الهاتف",
        usernamePlaceholder: "أدخل رقم الهاتف",
        password: "كلمة المرور",
        passwordPlaceholder: "أدخل كلمة المرور",
        rememberMe: "تذكرني",
        forgotPassword: "نسيت كلمة المرور؟",
        login: "تسجيل الدخول",
        loggingIn: "جاري تسجيل الدخول...",
        loginSuccess: "تم تسجيل الدخول بنجاح! جاري التحويل...",
        loginFailed: "فشل تسجيل الدخول",
        userNotFound: "المستخدم غير موجود. يرجى التحقق من اسم المستخدم.",
        loginError: "خطأ في تسجيل الدخول:",
        userDataError: "خطأ في بيانات المستخدم",
        guestDataTransferred: "تم الاحتفاظ بعناصر السلة والمفضلة الخاصة بك",
        invalidCredentials: "اسم المستخدم أو كلمة المرور غير صحيحة",
        notFound: "المستخدم غير موجود",
        serverError: "خطأ في الخادم. يرجى المحاولة لاحقاً",
        checkCredentials: "فشل تسجيل الدخول. يرجى التحقق من بيانات الاعتماد.",
    },
    en: {
        registerNew: "Register New Account",
        registerNewSub: "Click here if first time",
        existingAccount: "Login to Existing Account",
        username: "Phone Number",
        usernamePlaceholder: "Enter phone number",
        password: "Password",
        passwordPlaceholder: "Enter your password",
        rememberMe: "Remember me",
        forgotPassword: "Forgot password?",
        login: "Login",
        loggingIn: "Logging in...",
        loginSuccess: "Login successful! Redirecting...",
        loginFailed: "Login failed",
        userNotFound: "User not found. Please check your username.",
        loginError: "Login error:",
        userDataError: "User data error",
        guestDataTransferred: "Your cart and favorites have been retained",
        invalidCredentials: "Invalid username or password",
        notFound: "User not found",
        serverError: "Server error. Please try again later",
        checkCredentials: "Login failed. Please check your credentials.",
    },
};

const t = texts[language === "en" ? "en" : "ar"];

const Login = () => {
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);
    const [formData, setFormData] = useState({username: "", password: ""});

    const [loginUser, {isLoading}] = useLoginMutation();
    const navigate = useNavigate();
    const location = useLocation();
    const [messageApi, contextHolder] = message.useMessage();

    // After login, return to where the user came from (e.g. Cart) or home
    const from = location.state?.from || "/";

    const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData((prev) => ({...prev, [name]: value}));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await loginUser(formData).unwrap();

            if (response?.success !== 1) {
                const errorMessage = response?.msg || t.loginFailed;
                if (response?.success === 10) {
                    messageApi.error(t.userNotFound);
                } else if (response?.success === 20) {
                    messageApi.error(language === "ar"
                        ? "عفوا حسابك موقوف، يرجى التواصل مع الإدارة"
                        : "Your account is suspended. Please contact support.");
                } else {
                    messageApi.error(`${t.loginError} ${errorMessage}`);
                }
                return;
            }

            const userData = response?.user;
            if (!userData || !userData.contact_id) {
                messageApi.error(t.userDataError);
                return;
            }

            messageApi.success(t.loginSuccess);
            localStorage.setItem("user", JSON.stringify(userData));

            const transferSuccess = transferGuestDataToUser(userData.contact_id);
            if (transferSuccess) {
                messageApi.info(t.guestDataTransferred);
            }

            dispatch(reduxLogin());
            navigate(from, {replace: true});
        } catch (err) {
            console.error("Login failed:", err);
            if (err?.data?.msg) {
                messageApi.error(`${t.loginFailed}: ${err.data.msg}`);
            } else if (err?.status === 401) {
                messageApi.error(t.invalidCredentials);
            } else if (err?.status === 404) {
                messageApi.error(t.notFound);
            } else if (err?.status >= 500) {
                messageApi.error(t.serverError);
            } else {
                messageApi.error(t.checkCredentials);
            }
        }
    };

    return (
        <div
            dir="rtl"
            style={{
                minHeight: "100vh",
                background: "linear-gradient(135deg, #f5f0eb 0%, #ede8e3 100%)",
                fontFamily: "Cairo, sans-serif",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                paddingTop: "30px",
                paddingBottom: "80px",
            }}
        >
            <Helmet>
                <title>Meat-House El-Fardos - تسجيل الدخول</title>
                <meta name="description" content="Meat-House El-Fardos" />
            </Helmet>
            {contextHolder}

            {/* Logo */}
            <div style={{marginBottom: "24px", textAlign: "center"}}>
                <img
                    src={Logo}
                    alt="Meat House Al Fardous"
                    style={{width: "160px", height: "auto"}}
                />
            </div>

            {/* Register New Account Button */}
            <div style={{width: "100%", maxWidth: "420px", padding: "0 16px", marginBottom: "16px"}}>
                <Link to="/signUp">
                    <div
                        style={{
                            background: "linear-gradient(135deg, #c0392b 0%, #922b21 100%)",
                            border: "3px solid #f0c040",
                            borderRadius: "12px",
                            padding: "18px 24px",
                            textAlign: "center",
                            cursor: "pointer",
                            boxShadow: "0 4px 15px rgba(192,57,43,0.4)",
                            textDecoration: "none",
                            display: "block",
                        }}
                    >
                        <div
                            style={{
                                color: "#fff",
                                fontSize: "22px",
                                fontWeight: "bold",
                                lineHeight: 1.4,
                                textDecoration: "underline",
                            }}
                        >
                            {t.registerNew}
                        </div>
                        <div
                            style={{
                                color: "#fff",
                                fontSize: "20px",
                                fontWeight: "bold",
                                textDecoration: "underline",
                            }}
                        >
                            {t.registerNewSub}
                        </div>
                    </div>
                </Link>
            </div>

            {/* Login Card */}
            <div
                style={{
                    width: "100%",
                    maxWidth: "420px",
                    padding: "0 16px",
                }}
            >
                <div
                    style={{
                        background: "#fff",
                        borderRadius: "12px",
                        padding: "28px 24px 32px",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    }}
                >
                    <h2
                        style={{
                            textAlign: "center",
                            color: "#222",
                            fontSize: "22px",
                            fontWeight: "bold",
                            marginBottom: "24px",
                        }}
                    >
                        {t.existingAccount}
                    </h2>

                    <form onSubmit={handleSubmit}>
                        {/* Phone field */}
                        <div style={{marginBottom: "16px"}}>
                            <label
                                style={{
                                    display: "block",
                                    textAlign: "right",
                                    color: "#444",
                                    fontSize: "14px",
                                    marginBottom: "6px",
                                    fontWeight: "600",
                                }}
                            >
                                {t.username}
                            </label>
                            <div style={{position: "relative"}}>
                                <input
                                    name="username"
                                    type="text"
                                    required
                                    value={formData.username}
                                    onChange={handleChange}
                                    placeholder={t.usernamePlaceholder}
                                    style={{
                                        width: "100%",
                                        padding: "13px 16px 13px 44px",
                                        borderRadius: "8px",
                                        border: "1px solid #ddd",
                                        backgroundColor: "#f5f5f5",
                                        fontSize: "15px",
                                        color: "#888",
                                        boxSizing: "border-box",
                                        outline: "none",
                                        direction: "rtl",
                                        textAlign: "right",
                                    }}
                                />
                                {/* Phone icon on left */}
                                <span
                                    style={{
                                        position: "absolute",
                                        left: "12px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        color: "#c9a84c",
                                        fontSize: "18px",
                                    }}
                                >
                                    📱
                                </span>
                            </div>
                        </div>

                        {/* Password field */}
                        <div style={{marginBottom: "16px"}}>
                            <label
                                style={{
                                    display: "block",
                                    textAlign: "right",
                                    color: "#444",
                                    fontSize: "14px",
                                    marginBottom: "6px",
                                    fontWeight: "600",
                                }}
                            >
                                {t.password}
                            </label>
                            <div style={{position: "relative"}}>
                                <input
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    required
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder={t.passwordPlaceholder}
                                    style={{
                                        width: "100%",
                                        padding: "13px 16px 13px 80px",
                                        borderRadius: "8px",
                                        border: "1px solid #ddd",
                                        backgroundColor: "#f5f5f5",
                                        fontSize: "15px",
                                        color: "#888",
                                        boxSizing: "border-box",
                                        outline: "none",
                                        direction: "rtl",
                                        textAlign: "right",
                                    }}
                                />
                                {/* Key + Eye icons on left */}
                                <div
                                    style={{
                                        position: "absolute",
                                        left: "8px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        display: "flex",
                                        gap: "4px",
                                        alignItems: "center",
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={togglePasswordVisibility}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: "0",
                                            color: "#999",
                                            fontSize: "16px",
                                            lineHeight: 1,
                                        }}
                                    >
                                        {showPassword ? "🙈" : "🙈"}
                                    </button>
                                    <span style={{color: "#c9a84c", fontSize: "16px"}}>🔑</span>
                                </div>
                            </div>
                        </div>

                        {/* Remember me + Forgot password */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "20px",
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => {}}
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: "#666",
                                    fontSize: "13px",
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    padding: 0,
                                    fontFamily: "Cairo, sans-serif",
                                }}
                            >
                                {t.forgotPassword}
                            </button>
                            <label
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    color: "#444",
                                    fontSize: "13px",
                                    cursor: "pointer",
                                }}
                            >
                                {t.rememberMe}
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    style={{
                                        width: "16px",
                                        height: "16px",
                                        accentColor: "#c0392b",
                                    }}
                                />
                            </label>
                        </div>

                        {/* Login Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            style={{
                                width: "100%",
                                padding: "15px",
                                background: isLoading
                                    ? "#e57373"
                                    : "linear-gradient(135deg, #c0392b 0%, #922b21 100%)",
                                color: "#fff",
                                border: "none",
                                borderRadius: "10px",
                                fontSize: "18px",
                                fontWeight: "bold",
                                cursor: isLoading ? "not-allowed" : "pointer",
                                boxShadow: "0 3px 10px rgba(192,57,43,0.35)",
                                transition: "opacity 0.2s",
                                fontFamily: "Cairo, sans-serif",
                            }}
                        >
                            {isLoading ? t.loggingIn : t.login}
                        </button>
                    </form>
                </div>
            </div>

            {/* Bottom Navigation */}
            <div
                style={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: "#fff",
                    borderTop: "1px solid #eee",
                    display: "flex",
                    justifyContent: "space-around",
                    alignItems: "center",
                    padding: "10px 0 14px",
                    boxShadow: "0 -2px 10px rgba(0,0,0,0.08)",
                }}
            >
                <Link to="/" style={{color: "#444", textDecoration: "none", fontSize: "22px"}}>🏠</Link>
                <Link to="/" style={{color: "#444", textDecoration: "none", fontSize: "22px"}}>☰</Link>
                <Link to="/Login" style={{color: "#c9a84c", textDecoration: "none", fontSize: "22px"}}>👤</Link>
                <Link to="/" style={{color: "#444", textDecoration: "none", fontSize: "22px"}}>⚙️</Link>
            </div>
        </div>
    );
};

export default Login;
